import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-slate-200 overflow-hidden" }
const _hoisted_2 = { class: "lg:hidden mt-4 text-center" }
const _hoisted_3 = { class: "text-xs text-slate-500" }
const _hoisted_4 = { class: "font-semibold text-slate-700" }

import type { PaginacionFilterParams } from "@/utils/PaginacionFilterParams";
import { computed, ref } from "vue";
import ErrorState from "@/share/components/ErrorState.vue";
import { useMarcaVehiculosList } from "../composables/marcaVehiculosComposables";
import { useMarcaVehiculoCreate } from "../composables/marcaVehiculosComposables/useMarcaVehiculoCreate";
import { useMarcaVehiculoToggleVisib } from "../composables/marcaVehiculosComposables";
import { useMarcaVehiculoDelete } from "../composables/marcaVehiculosComposables";
import { useMarcaVehiculoUpdate } from "../composables/marcaVehiculosComposables";
import FormularioMarcaVehiculo from "../components/marcaVehiculoComponents/FormularioMarcaVehiculo.vue";
import MarcaVehiculoTable from "../components/marcaVehiculoComponents/MarcaVehiculoTable.vue";
import EmptyState from "../components/marcaVehiculoComponents/EmptyState.vue";
import SkeletonTablas from "@/share/components/SkeletonTablas.vue";
import PaginationCompontens from "@/share/components/PaginationCompontens.vue";
import FiltrosComponents from "@/share/components/filtrosComponents.vue";
import AdminPageShell from "@/share/components/admin/AdminPageShell.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'MarcaVehiculoView',
  setup(__props) {

const params = ref<PaginacionFilterParams>({
  page: 1,
  limit: 10,
  isVisible: undefined,
  search: "",
});

const sortField = ref<
  | "nombreMarca"
  | "descripcionMarca"
  | "isVisible"
  | "state"
  | "createdAt"
  | null
>(null);
const sortDirection = ref<"asc" | "desc">("asc");
const isFormularioOpen = ref(false);
const marcaEditId = ref<string | undefined>(undefined);
const isDelete = ref(false);

const {
  marcas,
  total,
  totalPages,
  currentPage,
  isPending: isLoadingList,
  isError,
  error,
  refetch,
} = useMarcaVehiculosList(params);

const { mutate: crearMarcaVehiculos, isPending: isCreatingMarcaVeh } =
  useMarcaVehiculoCreate(() => {
    isFormularioOpen.value = false;
  });
const { mutate: toggleVisibility } = useMarcaVehiculoToggleVisib();
const { mutate: deleteMarcaVehiculos } = useMarcaVehiculoDelete(() => {
  isDelete.value = true;
  setTimeout(() => (isDelete.value = false), 100);
});
const { mutate: updateMarcaVehiculos, isPending: isUpdatingMarcaVeh } =
  useMarcaVehiculoUpdate(() => {
    isFormularioOpen.value = false;
    marcaEditId.value = undefined;
  });

const isSavingMarcaVehiculo = computed(() =>
  marcaEditId.value ? isUpdatingMarcaVeh.value : isCreatingMarcaVeh.value
);

const marcasOrdenadas = computed(() => {
  // Copiamos el array reactivo para no mutarlo directamente
  const data = [...marcas.value];

  const field = sortField.value;
  if (field) {
    data.sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection.value === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection.value === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }

  return data;
});

const handleSearch = (search: string) => {
  params.value.search = search;
  params.value.page = 1;
};

const handleVisibilityFilter = (visibility: number | undefined) => {
  params.value.isVisible = visibility;
  params.value.page = 1;
};

const handlePageChange = (page: number) => {
  params.value.page = page;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleLimitChange = (limit: number) => {
  params.value.limit = limit;
  params.value.page = 1;
};

const handleSort = (
  field:
    | "nombreMarca"
    | "descripcionMarca"
    | "isVisible"
    | "state"
    | "createdAt"
) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const handleToggleVisibility = async (marcaId: string) => {
  toggleVisibility(marcaId);
};

const handleOpenCreate = () => {
  marcaEditId.value = undefined;
  isFormularioOpen.value = true;
};

const handleEditMarca = (marcaId: string) => {
  marcaEditId.value = marcaId;
  isFormularioOpen.value = true;
};

const handleSaveMarca = (formData: FormData) => {
  crearMarcaVehiculos(formData);
};

const handleUpdateMarca = (id: string, formData: FormData) => {
  updateMarcaVehiculos({
    idMarca: id,
    formData: formData,
  });
};

const handleCloseFormulario = () => {
  isFormularioOpen.value = false;
  marcaEditId.value = undefined;
};

const handleDeleteMarca = async (marcaId: string) => {
  deleteMarcaVehiculos(marcaId);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminPageShell, {
    title: "Marcas de vehículos",
    description: "Administra el catálogo de marcas de vehículos."
  }, {
    actions: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        onClick: handleOpenCreate,
        class: "w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          class: "w-5 h-5 flex-shrink-0",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 4v16m8-8H4"
          })
        ], -1),
        _createElementVNode("span", null, "Nueva marca", -1)
      ]))])
    ]),
    default: _withCtx(() => [
      _createVNode(FormularioMarcaVehiculo, {
        isOpen: isFormularioOpen.value,
        marcaId: marcaEditId.value,
        "is-saving": isSavingMarcaVehiculo.value,
        onClose: handleCloseFormulario,
        onSave: handleSaveMarca,
        onUpdate: handleUpdateMarca
      }, null, 8, ["isOpen", "marcaId", "is-saving"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FiltrosComponents, {
          search: params.value.search,
          visibility: params.value.isVisible,
          limit: params.value.limit,
          total: _unref(total),
          onSearch: handleSearch,
          onVisibilityFilter: handleVisibilityFilter,
          onLimitChange: handleLimitChange,
          onRefresh: _unref(refetch)
        }, null, 8, ["search", "visibility", "limit", "total", "onRefresh"]),
        (_unref(isLoadingList))
          ? (_openBlock(), _createBlock(SkeletonTablas, { key: 0 }))
          : (_unref(isError))
            ? (_openBlock(), _createBlock(ErrorState, {
                key: 1,
                error: _unref(error),
                onRetry: _unref(refetch)
              }, null, 8, ["error", "onRetry"]))
            : (_unref(marcas).length > 0)
              ? (_openBlock(), _createBlock(MarcaVehiculoTable, {
                  key: 2,
                  Marcas: marcasOrdenadas.value,
                  "sort-field": sortField.value,
                  "sort-direction": sortDirection.value,
                  currentPage: _unref(currentPage),
                  pageSize: params.value.limit,
                  isDelete: isDelete.value,
                  onSort: handleSort,
                  onToggleVisibility: handleToggleVisibility,
                  onEditCategoria: handleEditMarca,
                  onDeleteMarca: handleDeleteMarca,
                  onEditMarca: handleEditMarca
                }, null, 8, ["Marcas", "sort-field", "sort-direction", "currentPage", "pageSize", "isDelete"]))
              : (_openBlock(), _createBlock(EmptyState, { key: 3 })),
        (!_unref(isLoadingList) && !_unref(isError) && _unref(total) > 0)
          ? (_openBlock(), _createBlock(PaginationCompontens, {
              key: 4,
              "current-page": _unref(currentPage),
              "total-pages": _unref(totalPages),
              total: _unref(total),
              limit: params.value.limit!,
              onPageChange: handlePageChange
            }, null, 8, ["current-page", "total-pages", "total", "limit"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, [
          _cache[1] || (_cache[1] = _createTextVNode(" Total de marcas: ", -1)),
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(total)), 1)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})